import React, { useState } from 'react';
import {
	Box,
	Typography,
	Grid,
	Card,
	CardContent,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from '@mui/material';
import { Add, Remove, History, Warning } from '@mui/icons-material';
import { useLazyQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import Loader from '../generic/Loader';

const GET_INVENTORY = gql`
	query inventory($productId: String!, $shopId: String!, $locationId: String!) {
		inventory(productId: $productId, shopId: $shopId, locationId: $locationId) {
			id
			productId
			shopId
			locationId
			quantityOnHand
			quantityReserved
			quantityAvailable
			reorderLevel
			reorderQuantity
			lastRestockedAt
		}
	}
`;

const GET_INVENTORY_TRANSACTIONS = gql`
	query inventoryTransactions($productId: String!, $shopId: String!, $locationId: String!, $limit: Float) {
		inventoryTransactions(productId: $productId, shopId: $shopId, locationId: $locationId, limit: $limit) {
			id
			transactionType
			quantityChange
			quantityBefore
			quantityAfter
			referenceType
			referenceId
			notes
			createdBy
			createdAt
		}
	}
`;

const ADD_STOCK = gql`
	mutation addStock($input: AddStockInput!) {
		addStock(input: $input) {
			id
			quantityOnHand
		}
	}
`;

const ADJUST_INVENTORY = gql`
	mutation adjustInventory($input: AdjustInventoryInput!) {
		adjustInventory(input: $input) {
			id
			quantityOnHand
		}
	}
`;

interface InventoryTabProps {
	productId: number;
	shopId: string;
	locationId: string;
}

function InventoryTab({ productId, shopId, locationId }: InventoryTabProps) {
	const [inventory, setInventory] = useState<any>(null);
	const [transactions, setTransactions] = useState<any[]>([]);
	const [addStockDialogOpen, setAddStockDialogOpen] = useState(false);
	const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
	const [stockQuantity, setStockQuantity] = useState<number>(0);
	const [adjustQuantity, setAdjustQuantity] = useState<number>(0);
	const [adjustType, setAdjustType] = useState('ADJUSTMENT');
	const [notes, setNotes] = useState('');

	const [getInventory, { loading: loadingInventory }] = useLazyQuery(GET_INVENTORY, {
		onCompleted: (data) => {
			setInventory(data.inventory);
			if (data.inventory?.id) {
				loadTransactions();
			}
		},
	});

	const [getTransactions, { loading: loadingTransactions }] = useLazyQuery(
		GET_INVENTORY_TRANSACTIONS,
		{
			onCompleted: (data) => {
				setTransactions(data.inventoryTransactions || []);
			},
		}
	);

	const [addStock, { loading: addingStock }] = useMutation(ADD_STOCK, {
		onCompleted: () => {
			toast.success('Stock added successfully');
			setAddStockDialogOpen(false);
			setStockQuantity(0);
			setNotes('');
			loadInventoryData();
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});

	const [adjustInventory, { loading: adjusting }] = useMutation(ADJUST_INVENTORY, {
		onCompleted: () => {
			toast.success('Inventory adjusted successfully');
			setAdjustDialogOpen(false);
			setAdjustQuantity(0);
			setNotes('');
			loadInventoryData();
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});

	React.useEffect(() => {
		loadInventoryData();
	}, [productId, shopId, locationId]);

	const loadInventoryData = () => {
		getInventory({
			variables: {
				productId: String(productId),
				shopId,
				locationId,
			},
		});
	};

	const loadTransactions = () => {
		getTransactions({
			variables: {
				productId: String(productId),
				shopId,
				locationId,
				limit: 20,
			},
		});
	};

	const handleAddStock = () => {
		addStock({
			variables: {
				input: {
					productId: String(productId),
					shopId,
					locationId,
					quantity: stockQuantity,
					notes,
				},
			},
		});
	};

	const handleAdjustInventory = () => {
		adjustInventory({
			variables: {
				input: {
					productId: String(productId),
					shopId,
					locationId,
					quantityChange: adjustQuantity,
					transactionType: adjustType,
					notes,
				},
			},
		});
	};

	const isLowStock = inventory && inventory.quantityAvailable <= inventory.reorderLevel;

	return (
		<>
			<Loader isLoading={loadingInventory || loadingTransactions} />
			<Box>
				<Typography variant="h6" gutterBottom>
					Inventory Management
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
					View and manage inventory levels for this product. Stock changes must be done explicitly through Add Stock or Adjust Inventory actions.
				</Typography>

				{/* Current Stock Overview */}
				<Grid container spacing={3} sx={{ mb: 4 }}>
					<Grid item xs={12} md={3}>
						<Card sx={{ bgcolor: 'primary.lighter' }}>
							<CardContent>
								<Typography variant="body2" color="text.secondary">
									On Hand
								</Typography>
								<Typography variant="h4">
									{inventory?.quantityOnHand || 0}
								</Typography>
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12} md={3}>
						<Card sx={{ bgcolor: 'warning.lighter' }}>
							<CardContent>
								<Typography variant="body2" color="text.secondary">
									Reserved
								</Typography>
								<Typography variant="h4">
									{inventory?.quantityReserved || 0}
								</Typography>
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12} md={3}>
						<Card sx={{ bgcolor: isLowStock ? 'error.lighter' : 'success.lighter' }}>
							<CardContent>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<Typography variant="body2" color="text.secondary">
										Available
									</Typography>
									{isLowStock && <Warning fontSize="small" color="error" />}
								</Box>
								<Typography variant="h4" color={isLowStock ? 'error.main' : 'success.main'}>
									{inventory?.quantityAvailable || 0}
								</Typography>
								{isLowStock && (
									<Typography variant="caption" color="error">
										Low Stock Alert
									</Typography>
								)}
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12} md={3}>
						<Card>
							<CardContent>
								<Typography variant="body2" color="text.secondary">
									Reorder Level
								</Typography>
								<Typography variant="h4">
									{inventory?.reorderLevel || 0}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				</Grid>

				{/* Action Buttons */}
				<Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
					<Button
						variant="contained"
						startIcon={<Add />}
						onClick={() => setAddStockDialogOpen(true)}
					>
						Add Stock
					</Button>
					<Button
						variant="outlined"
						startIcon={<Remove />}
						onClick={() => setAdjustDialogOpen(true)}
					>
						Adjust Inventory
					</Button>
				</Box>

				{/* Transaction History */}
				<Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
						<History />
						<Typography variant="h6">Transaction History</Typography>
					</Box>

					<TableContainer component={Paper}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Date</TableCell>
									<TableCell>Type</TableCell>
									<TableCell align="right">Quantity Change</TableCell>
									<TableCell align="right">Before</TableCell>
									<TableCell align="right">After</TableCell>
									<TableCell>Notes</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{transactions.length === 0 ? (
									<TableRow>
										<TableCell colSpan={6} align="center">
											No transaction history available
										</TableCell>
									</TableRow>
								) : (
									transactions.map((transaction) => (
										<TableRow key={transaction.id}>
											<TableCell>
												{new Date(transaction.createdAt).toLocaleString()}
											</TableCell>
											<TableCell>
												<Chip
													label={transaction.transactionType}
													size="small"
													color={
														transaction.transactionType === 'PURCHASE'
															? 'success'
															: transaction.transactionType === 'SALE'
																? 'primary'
																: 'default'
													}
												/>
											</TableCell>
											<TableCell
												align="right"
												sx={{
													color:
														transaction.quantityChange > 0
															? 'success.main'
															: 'error.main',
												}}
											>
												{transaction.quantityChange > 0 ? '+' : ''}
												{transaction.quantityChange}
											</TableCell>
											<TableCell align="right">
												{transaction.quantityBefore}
											</TableCell>
											<TableCell align="right">
												{transaction.quantityAfter}
											</TableCell>
											<TableCell>{transaction.notes || '-'}</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>

				{/* Add Stock Dialog */}
				<Dialog open={addStockDialogOpen} onClose={() => setAddStockDialogOpen(false)} maxWidth="sm" fullWidth>
					<DialogTitle>Add Stock</DialogTitle>
					<DialogContent>
						<Box sx={{ pt: 2 }}>
							<TextField
								fullWidth
								required
								type="number"
								label="Quantity to Add"
								value={stockQuantity}
								onChange={(e) => setStockQuantity(parseInt(e.target.value) || 0)}
								sx={{ mb: 2 }}
								inputProps={{ min: 1 }}
							/>
							<TextField
								fullWidth
								multiline
								rows={3}
								label="Notes"
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								helperText="e.g., Purchase order #1234, Restock from supplier"
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setAddStockDialogOpen(false)}>Cancel</Button>
						<Button
							variant="contained"
							onClick={handleAddStock}
							disabled={stockQuantity <= 0 || addingStock}
						>
							Add Stock
						</Button>
					</DialogActions>
				</Dialog>

				{/* Adjust Inventory Dialog */}
				<Dialog open={adjustDialogOpen} onClose={() => setAdjustDialogOpen(false)} maxWidth="sm" fullWidth>
					<DialogTitle>Adjust Inventory</DialogTitle>
					<DialogContent>
						<Box sx={{ pt: 2 }}>
							<FormControl fullWidth sx={{ mb: 2 }}>
								<InputLabel>Adjustment Type</InputLabel>
								<Select
									value={adjustType}
									label="Adjustment Type"
									onChange={(e) => setAdjustType(e.target.value)}
								>
									<MenuItem value="ADJUSTMENT">Manual Adjustment</MenuItem>
									<MenuItem value="DAMAGE">Damage/Loss</MenuItem>
									<MenuItem value="RETURN">Customer Return</MenuItem>
								</Select>
							</FormControl>

							<TextField
								fullWidth
								required
								type="number"
								label="Quantity Change"
								value={adjustQuantity}
								onChange={(e) => setAdjustQuantity(parseInt(e.target.value) || 0)}
								sx={{ mb: 2 }}
								helperText="Use negative numbers to reduce stock (e.g., -5 for 5 damaged items)"
							/>

							<TextField
								fullWidth
								multiline
								rows={3}
								label="Notes"
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								helperText="Reason for adjustment"
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
						<Button
							variant="contained"
							onClick={handleAdjustInventory}
							disabled={adjustQuantity === 0 || adjusting}
						>
							Adjust Inventory
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</>
	);
}

export default InventoryTab;
